import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Mail, Clock } from "lucide-react";
import { LegalNav } from "@/pages/Privacy";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

/**
 * ShiftQuote Support / Contact / FAQ — publicly accessible (no login).
 * Linked from Privacy + Terms + App Store / Play Console listings.
 * Single contact address: hello@shiftquote.app
 */
export default function Support() {
  // Referral Program FAQ only while the backend REFERRALS_ENABLED flag is on.
  const { referralsEnabled } = useFeatureFlags();
  const groups = FAQ_GROUPS.filter((g) => referralsEnabled || !g.referralProgram);
  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-200">
      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] mb-6"
          data-testid="support-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back to app
        </Link>
        <LegalNav active="support" />
        <div className="label-eyebrow text-[#FF3B30] mb-3">SUPPORT</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-2">
          Real humans. Real answers.
        </h1>
        <p className="text-zinc-500 text-sm mb-10">
          Skim the FAQ below or email us. One founder, one inbox — we read every message.
        </p>

        {/* Contact card */}
        <div className="border border-white/10 bg-[#121214] p-6 mb-10" data-testid="support-contact">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF3B30]/15 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <div className="flex-1">
              <div className="font-display text-xl">Contact us</div>
              <a
                className="text-[#FF3B30] underline text-sm break-all"
                href="mailto:hello@shiftquote.app"
                data-testid="support-contact-email"
              >
                hello@shiftquote.app
              </a>
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-2">
                <Clock className="w-3 h-3" /> Typical response: 24&ndash;48 hours
              </div>
            </div>
          </div>
        </div>

        {groups.map((g) => (
          <FaqGroup key={g.title} title={g.title} items={g.items} />
        ))}

        <div className="mt-12 text-center text-xs text-zinc-600">
          Still stuck? Email{" "}
          <a className="text-[#FF3B30] underline" href="mailto:hello@shiftquote.app" data-testid="support-footer-email">
            hello@shiftquote.app
          </a>{" "}
          with a screenshot. We&apos;ll get back fast.
        </div>
      </main>
    </div>
  );
}

const FAQ_GROUPS = [
  {
    title: "General",
    items: [
      {
        q: "What is ShiftQuote?",
        a: "A private-by-design car-repair marketplace. Snap your problem, post it, get private quotes from local verified shops. No phone calls. No appointments. No giving your name to a stranger before you know what the job costs.",
      },
      {
        q: "Is it actually free?",
        a: "For most drivers, yes — posting jobs, browsing shops, messaging, manual mileage logs, and basic reminders are free forever. Paid stuff is opt-in: Premium $3.99/mo (auto-track + Profit Radar + Receipt Vault + True Clock) with a 7-day free trial, and the Urgent Button $6.99 one-time unlock. Nothing you don't ask for ever gets charged.",
      },
      {
        q: "How does the anonymity work?",
        a: "Shops see your photos, vehicle, and a randomized handle (like \"Owner-3742\") — never your name, email, exact address, or phone. Identity only swaps after you accept a quote. Until then, no shop can find you.",
      },
      {
        q: "Where can I use ShiftQuote?",
        a: "Web (shiftquote.app), iOS, and Android. Same account everywhere. Repair shop search currently focuses on the United States; some features are US-only.",
      },
    ],
  },
  {
    title: "Subscriptions & Pricing",
    items: [
      {
        q: "What do the two driver tiers give me?",
        a: "FREE: post jobs, message shops, log mileage manually, basic service reminders. PREMIUM ($3.99/mo): everything in Free plus background-GPS Auto-Track, Profit Radar (real hourly rate after gas + taxes), the True Clock shift timer, and the Receipt Vault (photo storage + OCR + tax-ready CSV export). 7-day free trial included.",
      },
      {
        q: "Why two tiers instead of one?",
        a: "Most people just want a place to post a repair and get a quote — that should be free. Premium only matters if you drive for work or want to actually know what you make per hour. We didn't want to gate the basics behind a paywall.",
      },
      {
        q: "Are prices the same in every country?",
        a: "We use Apple App Store and Google Play's regional pricing. The price you see in-app is your local price, set by the store to roughly match $3.99 USD. Taxes are applied per local rules.",
      },
      {
        q: "How do I cancel?",
        a: "iOS: Settings → Apple ID → Subscriptions → ShiftQuote → Cancel. Android: Google Play → Profile → Payments & subscriptions → Subscriptions → ShiftQuote → Cancel. Web: Settings → Subscription → Manage. Flipping a toggle off inside the app does NOT cancel — you have to use the store. We can't cancel for you (Apple/Google rules).",
      },
      {
        q: "What if I cancel mid-month?",
        a: "You keep Premium access through the end of the period you already paid for. No prorated refunds — that's Apple/Google policy, not ours. You can re-subscribe anytime.",
      },
      {
        q: "Will you ever raise prices?",
        a: "If we do, we'll email you at least 30 days before it hits. Your current price stays locked until you cancel or your platform forces a re-grant.",
      },
    ],
  },
  {
    title: "Mileage Tracker",
    items: [
      {
        q: "Manual vs. Auto-Track — what's the difference?",
        a: "Manual (FREE): you tap Start when a trip begins, tap Stop when it ends, or just type the distance / odometer. Auto-Track (Premium $3.99/mo): your phone records every trip in the background using GPS, even when the app is closed. You don't have to remember anything.",
      },
      {
        q: "Will Auto-Track destroy my battery?",
        a: "No. We use motion detection and a 50m+ distance filter so GPS isn't polling constantly. On modern phones, expect ~3-5% extra battery per day. You can turn it off anytime from the Mileage page.",
      },
      {
        q: "What permissions does Auto-Track need?",
        a: "iOS: \"Always Allow\" location. Android: ACCESS_BACKGROUND_LOCATION. We only ask the first time you flip the Auto-Track toggle on — never before.",
      },
      {
        q: "How does the free trial work?",
        a: "First time you enable Auto-Track, a 7-day free trial of Premium kicks in. Full feature access during the trial. After day 7, the toggle is gated behind the $3.99/mo subscription. Cancel during the trial and you owe nothing.",
      },
      {
        q: "Can I export my mileage for taxes?",
        a: "Yes. Tap Export Tax Report on the Mileage page and pick a date range to download a PDF of your logged trips. Premium subscribers can also include a Maintenance section from their receipts in the same report. Bring it to your accountant.",
      },
      {
        q: "I disabled Auto-Track. Why am I still being charged?",
        a: "Disabling the toggle doesn't cancel the subscription — you have to cancel through the App Store / Play Store / Stripe Portal (see \"How do I cancel?\" above). We made this explicit so people don't accidentally lose access mid-month.",
      },
    ],
  },
  {
    title: "Profit Radar (Premium)",
    items: [
      {
        q: "What is Profit Radar?",
        a: "A tool that shows what you ACTUALLY make per hour after gas, tolls, and a rough tax estimate — not just the gross number the app you drive for shows you. You log a shift, your earnings, and your fuel cost. We do the math.",
      },
      {
        q: "How do you calculate hourly rate?",
        a: "Literal hourly rate = (Gross earnings + tips - gas - tolls - your tax %) &divide; (Shift duration in hours). Tax percentage is whatever YOU set in Settings (we suggest 25% as a starting point). It's a calculator, not financial or tax advice.",
      },
      {
        q: "Is this tax advice?",
        a: "No. Nothing here is tax, financial, or legal advice. Profit Radar is a calculator that uses numbers you enter to estimate your real take-home. Talk to an accountant for actual tax filing.",
      },
      {
        q: "Why is Profit Radar gated to Premium?",
        a: "Because honestly tracking what you make takes a real product, and we'd rather charge $3.99/mo to a small group than ruin the free experience with ads. If you don't drive for work, you probably don't need it.",
      },
      {
        q: "Where does my Profit Radar data go?",
        a: "Stays on our server attached to your account, visible only to you. We don't sell it, we don't share it with shops, we don't train models on it. Delete your account and your Profit Radar data is deleted immediately.",
      },
    ],
  },
  {
    title: "The True Clock (Premium)",
    items: [
      {
        q: "What is The True Clock?",
        a: "A live shift timer that runs while you drive. Tap Start at the beginning of your shift, tap Stop at the end. The card shows your in-progress literal hourly rate as it updates. It's the same Profit Radar math, but live.",
      },
      {
        q: "Does The True Clock use GPS?",
        a: "No. It's a stopwatch with math attached. No location, no background tracking. Just elapsed time + the earnings you enter.",
      },
      {
        q: "What if I forget to stop the clock?",
        a: "Open the app, hit Stop, and edit the end time to when you actually finished. The shift entry stores both the timer end and your edited end — only the edited one is used for the rate calc.",
      },
    ],
  },
  {
    title: "Urgent Button ($6.99 one-time unlock)",
    items: [
      {
        q: "What is the Urgent Button?",
        a: "A broadcast that pings ALL verified shops within 25 miles of your location — bypassing tier visibility limits. Use it for flat tires, breakdowns, \"need help right now\" stuff.",
      },
      {
        q: "Why $6.99?",
        a: "It's a one-time UNLOCK on your account — not a per-use charge. Pay once, the Urgent option is available to you whenever you need it (still rate-limited to 1 broadcast per 24 hours so shops don't get spammed). The fee covers broadcast infrastructure and discourages misuse.",
      },
      {
        q: "How fast do shops respond?",
        a: "Most urgent alerts get a first reply within 5-15 minutes during business hours. We CAN'T guarantee a response — shops decide whether to take the job. The $6.99 pays for the broadcast, not for a guaranteed repair.",
      },
      {
        q: "Why is there a 1-per-24-hour limit?",
        a: "Protects shop owners from spam. If you genuinely need a second urgent broadcast in 24 hours, email hello@shiftquote.app and we'll review case-by-case.",
      },
      {
        q: "Can I get a refund?",
        a: "The $6.99 unlock is non-refundable once your account is flagged urgent-enabled. If the very first broadcast fails to dispatch (e.g., no shops in radius), we auto-refund. Apple/Google can also refund per their own policies.",
      },
    ],
  },
  {
    title: "Receipt Vault (Premium)",
    items: [
      {
        q: "What does the Receipt Vault do?",
        a: "Snap a photo of any gas / repair / parts / car-wash receipt and we store it tagged by category and date. We also OCR the photo to pull out the dollar amount, shop name, and date so you don't have to type them.",
      },
      {
        q: "Is the OCR perfect?",
        a: "No OCR is. We extract what we can from the photo — you can edit any field before saving. The photo itself is always retained as the source of truth.",
      },
      {
        q: "Can I export receipts for taxes?",
        a: "Premium subscribers can include their receipts as a Maintenance section in the Export Tax Report PDF on the Mileage page. There is no separate one-tap receipts download yet; email hello@shiftquote.app if you need a copy of your receipt data.",
      },
      {
        q: "Where are receipt photos stored?",
        a: "On our private object storage, scoped to your account only. Nobody but you can see them. Deleting your account deletes your receipts, and the photos and files you uploaded are detached from your account; email hello@shiftquote.app if you also want stored copies purged.",
      },
    ],
  },
  {
    title: "Referral Program (US only)",
    referralProgram: true, // hidden unless REFERRALS_ENABLED (see Support())
    items: [
      {
        q: "How does it work?",
        a: "Share your referral link with repair shop owners. If they sign up and subscribe, you earn $10/month per Pro shop and $15/month per Premium shop — lifetime, as long as their subscription stays active.",
      },
      {
        q: "Why US-only?",
        a: "Tax forms (1099) and payout regulations are complicated outside the US. We may expand later; right now, only accounts registered in the United States can earn referral commissions.",
      },
      {
        q: "When do commissions start?",
        a: "Once a referred shop's first paid month is collected (after their 30-day free trial converts). Trial periods don't generate commissions.",
      },
      {
        q: "When do I get paid?",
        a: "Payouts launch once Stripe Connect is wired up (coming soon). Until then, earnings accrue in the dashboard with \"lifetime earned\" + \"monthly recurring\" views. Minimum payout: $25. US referrers earning > $600/year will receive a 1099.",
      },
    ],
  },
  {
    title: "Account & Privacy",
    items: [
      {
        q: "How do I delete my account?",
        a: "In the app go to Settings → Privacy → \"Delete account\". This immediately deletes your mileage log and Auto-Track trips, parking spots, vehicles, receipts, Profit Radar data, reminders, push tokens, sessions, job posts (owners) and bids (shops), cancels any web subscription, and anonymizes your account record (name, email, phone, location). Messages you sent stay visible to the other person as \"(deleted user)\". Photos and files you uploaded are detached from your account; email hello@shiftquote.app if you also want stored copies purged. Apple / Google subscriptions must be cancelled in your store account. You can also email hello@shiftquote.app to request deletion.",
      },
      {
        q: "What data do you actually collect?",
        a: "Email, password hash, role, foreground GPS (while app is open), background GPS (only with Premium Auto-Track and your explicit \"Always\" permission), photos / videos on your posts, messages, mileage entries, Profit Radar entries, receipt photos, and push tokens. Full breakdown at /privacy.",
      },
      {
        q: "Does ShiftQuote track me all the time?",
        a: "No. Foreground GPS only while the app is open. Background GPS only if you've enabled Auto-Track AND have an active Premium subscription. Revoke anytime in device Settings.",
      },
      {
        q: "Do you sell my data?",
        a: "Never. No ad networks, no data brokers, no AI model training on your content. Period.",
      },
    ],
  },
  {
    title: "For Repair Shops",
    items: [
      {
        q: "How do shops sign up?",
        a: "ShiftQuote → Sign Up → choose \"Repair Shop\" → fill out the business application. Most applications reviewed within 24 hours.",
      },
      {
        q: "What does it cost shops?",
        a: "Free tier: see 1 job at a time in your radius. Pro ($59.99/mo): up to 3 jobs visible. Premium ($79.99/mo): unlimited jobs + priority placement. Every new shop gets a 30-day free trial of either paid tier. (Note: these are SHOP prices — not the driver-side $3.99/mo Premium tier above.)",
      },
      {
        q: "Can I see a demo first?",
        a: "Yes. Email hello@shiftquote.app to book a 10-minute live demo. We have a built-in demo mode that walks you through the shop dashboard with realistic mock leads.",
      },
      {
        q: "Do you take a cut of repair work?",
        a: "Zero. Whatever you quote, you collect. Owner pays you directly, off-platform. The only revenue we take from shops is the optional subscription.",
      },
    ],
  },
];

function FaqGroup({ title, items }) {
  return (
    <section className="mb-10" data-testid={`faq-group-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="label-eyebrow text-zinc-500 mb-4">{title.toUpperCase()}</div>
      <div className="space-y-2">
        {items.map((item, i) => <FaqItem key={i} {...item} />)}
      </div>
    </section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-[#121214]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02]"
        data-testid="faq-question"
      >
        <span className="text-sm sm:text-base">{q}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-zinc-300 leading-relaxed" data-testid="faq-answer">
          {a}
        </div>
      )}
    </div>
  );
}
